/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {RefObject, useCallback, useLayoutEffect, useRef} from 'react';
import isFunction from 'lodash.isfunction';
import isUndefined from 'lodash.isundefined';

interface FilterChecklistLayoutPosition {
  left: number;
  top: number;
}

type FilterChecklistLayoutPositions = Record<string, FilterChecklistLayoutPosition>;

interface FilterChecklistLayoutAnimation {
  capturePositions: () => void;
  containerRef: RefObject<HTMLDivElement>;
}

const chipSelector = '[data-filter-checklist-key]';
const animationOptions = {
  duration : 220,
  easing   : 'cubic-bezier(0.2, 0, 0, 1)'
};

/**
 * Animates checklist chips from their previous positions to the current layout.
 *
 * @param container         Defines the checklist chip container.
 * @param previousPositions Defines positions from the previous render.
 * @param reduceMotion      Disables movement when reduced motion is enabled.
 * @returns Positions measured from the current layout.
 */
export const animateFilterChecklistLayout = (
  container: HTMLElement,
  previousPositions: FilterChecklistLayoutPositions,
  reduceMotion: boolean
): FilterChecklistLayoutPositions => {
  const currentPositions: FilterChecklistLayoutPositions = {};
  const chips = Array.from(container.querySelectorAll<HTMLElement>(chipSelector));
  chips.forEach(chip => {
    const key = chip.dataset.filterChecklistKey;
    if (key) {
      const {left, top} = chip.getBoundingClientRect();
      const previousPosition = previousPositions[key];
      currentPositions[key] = {left, top};
      if (previousPosition && !reduceMotion && isFunction(chip.animate)) {
        const translateX = previousPosition.left - left;
        const translateY = previousPosition.top - top;
        if (translateX || translateY) {
          chip.animate([{
            transform : `translate3d(${translateX}px, ${translateY}px, 0)`
          }, {
            transform : 'translate3d(0, 0, 0)'
          }], animationOptions);
        }
      }
    }
  });
  return currentPositions;
};

/**
 * Tracks checklist chip positions and animates layout changes after rendering.
 *
 * ```typescript
 * import useFilterChecklistLayoutAnimation from './useFilterChecklistLayoutAnimation';
 *
 * ...
 *
 * const {capturePositions, containerRef} = useFilterChecklistLayoutAnimation();
 * ```
 */
export const useFilterChecklistLayoutAnimation = (): FilterChecklistLayoutAnimation => {
  const containerRef = useRef<HTMLDivElement>(null);
  const positionsRef = useRef<FilterChecklistLayoutPositions>({});

  const capturePositions = useCallback(() => {
    if (containerRef.current) {
      positionsRef.current = animateFilterChecklistLayout(
        containerRef.current,
        {},
        true
      );
    }
  }, []);

  useLayoutEffect(() => {
    if (containerRef.current) {
      const currentWindow = globalThis.window;
      const reducedMotionPreferred = !isUndefined(currentWindow) &&
        isFunction(currentWindow.matchMedia) &&
        currentWindow.matchMedia('(prefers-reduced-motion: reduce)').matches;
      positionsRef.current = animateFilterChecklistLayout(
        containerRef.current,
        positionsRef.current,
        reducedMotionPreferred
      );
    }
  });

  return {
    capturePositions,
    containerRef
  };
};

export default useFilterChecklistLayoutAnimation;
